import 'requestanimationframe';
import throttle    from 'raf-throttle';
import addClass    from 'add-class';
import removeClass from 'remove-class';
import delegate    from 'delegate';
import extend      from 'just-extend';
import Scroll      from 'scroll-js';
import {transitionProperty, transformProperty, transformCSSProperty} from './vendor-prefixes';

/**
 * The default options
 */
const defaultOptions = {
	dragSpeed: 0.5,     // Positions per second
	transitionSpeed: 0.5, // Seconds to transition items when clicked
	timingFunction: 'ease',
	itemSpacing: 8, // Pixels of distance between items
	inactiveScale: 0.7, // Scale of inactive items
	inactivePositionThreshold: 1,
	defaultItemIndex: 0,
	scrollToTopAfterTap: false,
	onChange: function() {},
};

// TODO: listen for window resize and recalculate item widths and spacing

/**
 * Allows the user to
 */
class CarouselSelector {

	constructor(element, options) {
		this.element = element;

		// Set the options with defaults
		this.options = extend({}, defaultOptions, options);

		// Set state defaults
		this.dragging = false;
		this.scheduledAnimationFrame = false;
		this.transitionsEnabled = false;

		// Set cached values and constants
		this.transitionValue = `${transformCSSProperty} ${this.options.transitionSpeed}s ${this.options.timingFunction}`;
		this.containerClass = 'carousel-selector-container';
		this.itemClass = 'carousel-selector-item';

		// Bind functions
		this.updateTransforms   = this.updateTransforms.bind(this);
		this.disableTransitions = this.disableTransitions.bind(this);
		this.enableTransitions  = this.enableTransitions.bind(this);
		this.dragStart          = this.dragStart.bind(this);
		this.drag               = this.drag.bind(this);
		this.dragEnd            = this.dragEnd.bind(this);
		this.itemClicked        = this.itemClicked.bind(this);

		// Create the basic structure
		this.initStructure();

		// Initialize the items
		this.initItems();

		// Bind the DOM events
		this.bindEvents();

		this.scroll = new Scroll(document.body);
	}

	initStructure() {
		// Wrap all the content with an inner containing element so we don't
		//   interfere with the styling of the styling of the main element.
		this.containerElement = document.createElement('div');
		this.containerElement.style.position = 'relative';
		this.containerElement.style.width = '100%';
		this.containerElement.style.overflow = 'hidden';
		addClass(this.containerElement, this.containerClass);

		var elements = [];

		for (let i = this.element.children.length - 1; i >= 0; i--) {
			let el = this.element.children[i];
			this.element.removeChild(el);
			elements.unshift(el);
		}

		for (let el of elements)
			this.containerElement.appendChild(el);

		this.element.appendChild(this.containerElement);
	}

	initItems() {
		this.items = [];

		const elements = [].slice.call(this.containerElement.children);

		if (!elements.length)
			throw new Error('No child elements were found.');

		const width = elements[0].scrollWidth;
		this.itemWidth = width;

		const even = (elements.length % 2 === 0);
		const len = elements.length;
		this.lowerLimit = (even) ? -len / 2    : -Math.floor(len / 2);
		this.upperLimit = (even) ? len / 2 - 1 :  Math.floor(len / 2);

		var position = this.lowerLimit;
		for (let el of elements) {
			el.style.position = 'absolute';
			el.style.top = 0;
			el.style.left = '50%';
			el.style.marginLeft = -(width / 2) + 'px';

			addClass(el, this.itemClass);

			this.items.push({
				element: el,
				position: position++
			});
		}

		this.selectItem(this.items[this.options.defaultItemIndex]);

		this.updateHeight();
	}

	bindEvents() {
		const body = document.querySelector('body');
		body.addEventListener('mousemove', this.drag, false);
		body.addEventListener('touchmove', this.drag, false);
		body.addEventListener('mouseup',   this.dragEnd, false);
		body.addEventListener('touchend',  this.dragEnd, false);

		this.containerElement.addEventListener('mousedown',  this.dragStart, false);
		this.containerElement.addEventListener('touchstart', this.dragStart, false);

		delegate(this.containerElement, '.' + this.itemClass, 'click', this.itemClicked);

		window.addEventListener('resize', throttle((event) => {
			this.updateHeight();
			this.updateItemWidth();
			this.updateTransforms();
		}));
	}

	count() {
		return this.items.length;
	}

	getActiveIndex() {
		for (let i = 0; i < this.items.length; i++) {
			if (this.items[i].position === 0)
				return i;
		}
	}

	getActiveItem() {
		return this.items[this.getActiveIndex()];
	}

	fireChange() {
		const index = this.getActiveIndex();
		const element = this.items[index].element;
		this.options.onChange(element, index);
	}

	scrollToTop() {
		this.scroll.to(0, this.element.offsetTop, {easing: 'easeInOutCubic', duration: 500});
	}

	disable() {
		this.disabled = true;
	}

	enable() {
		this.disabled = false;
	}

	dragStart(event) {
		if (this.disabled)
			return;

		// We don't want transitions on while they're dragging
		if (this.transitionsEnabled)
			this.disableTransitions();

		this.removeActiveClass();
		this.fixTouchEvents(event);
		this.dragX = event.pageX; // Save the current x position for next time
		this.dragTime = Date.now();
		this.dragging = true;
		this.dragged = false;
	}

	drag(event) {
		if (this.dragging) {
			this.dragged = true;
			this.fixTouchEvents(event);

			// Figure out the change in time, x, and position
			var deltaTime = (Date.now() - this.dragTime) / 1000; // In seconds
			var deltaX = event.pageX - this.dragX; // In pixels
			var deltaPosition = deltaX * this.options.dragSpeed * deltaTime;

			// Save the current time and x for next time
			this.dragX = event.pageX;
			this.dragTime = Date.now();

			// Change positions
			this.updatePositions(deltaPosition);

			// Schedule an update
			this.scheduleTransformsUpdate();
		}
	}

	dragEnd(event) {
		if (this.dragging) {
			this.dragging = false;
			if (this.dragged) {
				this.snap();
				this.fireChange();
				this.applyActiveClass();
				this.enableTransitions();
				this.scheduleTransformsUpdate();
			}
		}
	}

	itemClicked(event) {
		// Don't do anything if this button group is disabled
		if (this.disabled)
			return;

		for (let item of this.items) {
			if (item.element === event.delegateTarget) {
				this.selectItem(item, true);
				this.fireChange();

				if(this.options.scrollToTopAfterTap) this.scrollToTop();

				event.stopPropagation();
				return;
			}
		}
	}

	selectItem(item, withTransition) {
		// Change positions
		var deltaPosition = -item.position;
		this.updatePositions(deltaPosition);

		// Enable transitions if specified
		if (withTransition)
			this.enableTransitions();

		// Schedule an update
		this.scheduleTransformsUpdate();

		// Update which item has the active class
		this.applyActiveClass();
	}

	removeActiveClass() {
		for (let item of this.items)
			removeClass(item.element, 'active');
	}

	applyActiveClass() {
		for (let item of this.items) {
			if (item.position === 0)
				addClass(item.element, 'active');
			else
				removeClass(item.element, 'active');
		}
	}

	scheduleTransformsUpdate() {
		this.scheduledAnimationFrame = true;
		window.requestAnimationFrame(this.updateTransforms);
	}

	updateTransforms() {
		var items = this.items.slice();

		// Sort the items by ascending position.
		items.sort(function(a, b) {
			return a.position - b.position;
		});

		// Find the index of the item closest to position 0
		var absoluteMin = Number.POSITIVE_INFINITY;
		var centermostItemIndex;
		for (let i = 0; i < items.length; i++) {
			if (Math.abs(items[i].position) < absoluteMin) {
				absoluteMin = Math.abs(items[i].position);
				centermostItemIndex = i;
			}
		}

		// Split into two arrays, each with the center item at the beginning;
		//   that is, we will have two arrays that start at the center item and
		//   move out to the negative and positive extremes.
		var negatives = items.slice(0, centermostItemIndex);
		var positives = items.slice(centermostItemIndex);
		negatives.push(items[centermostItemIndex]);
		negatives.reverse();

		// Now determine the size and positions of all the items relative to the center item
		var centerItem = positives[0];

		centerItem._scale = this.getScaleAt(centerItem.position);
		centerItem._xOffset = centerItem.position * this.itemWidth;

		// We need to nudge the center item one way or the other because the centermost
		//   and second-to-centermost items need to share the gap in between them so that
		//   when they are at positions -0.5 and 0.5, they are mirrored across the center
		//   line.  As the center item approaches -0.5, it should be nudged further towards
		//   the positive--to account for this sharing--and vice versa.  If the center
		//   item is at 0, there is no nudge.
		var nudge = -centerItem.position * this.options.itemSpacing;
		centerItem._xOffset += nudge;

		this._calculateScaleAndOffsets(positives,  1);
		this._calculateScaleAndOffsets(negatives, -1);

		// Loop through the items and update the transformations
		for (let item of this.items) {
			if (item.didJump) {
				item.didJump = false;
				this.disableTransitionsOnItem(item);
			}

			item.element.style[transformProperty] = `translateX(${item._xOffset}px) scale(${item._scale})`;
		}

		// See if we need to disable transitions after it's over
		if (this.transitionsEnabled) {
			setTimeout(() => {
				this.disableTransitions();
			}, this.options.transitionSpeed + 20);
		}

		// Mark that we're open for business again
		this.scheduledAnimationFrame = false;
	}

	_calculateScaleAndOffsets(items, direction) {
		if (items.length <= 1)
			return;

		const itemSpacing = this.options.itemSpacing;
		const halfWidth = this.itemWidth / 2;

		for (let i = 1; i < items.length; i++) {
			var previousOffsetPlusRadius = items[i - 1]._xOffset + direction * items[i - 1]._scale * halfWidth;

			items[i]._scale = this.getScaleAt(items[i].position);
			items[i]._xOffset = previousOffsetPlusRadius + direction * (itemSpacing + items[i]._scale * halfWidth);
		}
	}

	updatePositions(deltaPosition) {
		for (let item of this.items) {
			item.position += deltaPosition;

			// Check for overflow, and wrap if necessary
			if (item.position > this.upperLimit) {
				item.position -= this.items.length;
				item.didJump = true;
			}
			else if (item.position <= this.lowerLimit) {
				item.position += this.items.length;
				item.didJump = true;
			}
		}
	}

	updateHeight() {
		this.containerElement.style.height = this.items[0].element.scrollHeight + 'px';
	}

	updateItemWidth() {
		const width = this.items[0].element.scrollWidth;
		this.itemWidth = width;
		for (let item of this.items)
			item.element.style.marginLeft = -(width / 2) + 'px';
	}

	getScaleAt(position) {
		var absPosition = Math.abs(position);
		if (absPosition < this.options.inactivePositionThreshold)
			return Math.pow(this.options.inactiveScale, absPosition);
		else
			return this.options.inactiveScale;
	}

	getXAt(position) {
		return position * (this.itemWidth + this.options.itemSpacing);
	}

	snap() {
		var overflow = this.items[0].position - Math.floor(this.items[0].position);
		var snapFunction = (overflow > 0.5) ? Math.ceil : Math.floor;

		for (let item of this.items)
			item.position = snapFunction(item.position);
	}

	disableTransitions() {
		for (let item of this.items)
			this.disableTransitionsOnItem(item);
		this.transitionsEnabled = false;
	}

	enableTransitions() {
		this.transitionsEnabled = true;
		for (let item of this.items)
			this.enableTransitionOnItem(item);
	}

	disableTransitionsOnItem(item) {
		item.element.style[transitionProperty] = 'unset';
	}

	enableTransitionOnItem(item) {
		item.element.style[transitionProperty] = this.transitionValue;
	}

	fixTouchEvents(event) {
		if (event.pageX === undefined)
			event.pageX = event.touches[0].pageX;
	}

	static create() {
		return new CarouselSelector(...arguments);
	}

}

export default CarouselSelector;



// WEBPACK FOOTER //
// ./assets/scripts/carousel-selector.js